import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GrLeaderboardResult } from '@/lib/api';

interface UseLeaderboardReturn {
  data: GrLeaderboardResult | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useLeaderboard(
  gameConfigId: string | null,
  gameEvent: string | null,
): UseLeaderboardReturn {
  const [data, setData] = useState<GrLeaderboardResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetch = useCallback(async () => {
    if (!gameConfigId || !gameEvent) {
      setData(null);
      setLoading(false);
      return;
    }
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const result = await api.getGameRewardsLeaderboard(gameConfigId, gameEvent);
      if (fetchIdRef.current !== id) return;
      setData(result);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load leaderboard');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, [gameConfigId, gameEvent]);

  const refresh = useCallback(() => {
    setError(null);
    fetch();
  }, [fetch]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return { data, loading, error, refresh };
}
